import { useContext, useMemo, forwardRef } from 'react';
import { format as formatFn, formatDistanceToNow } from 'date-fns';

import { DateTimeFormatKey } from 'types';

import { logError } from 'shared-services/logger-service';

import { LocaleContext } from 'context/LocaleContext';
import { useUpdatingMemo } from 'utils/hooks';

interface DateTimeProps {
    format?: DateTimeFormatKey,
    children: React.ReactNode | Date,
    fromNow?: boolean,
    onClick?: () => void
}

const DateTime = forwardRef<HTMLTimeElement, DateTimeProps>(({ format, fromNow, children, onClick }, ref) => {
    const localeCtx = useContext(LocaleContext);

    const dateValue: Date = useUpdatingMemo(() => {
        let value = children;

        if (Array.isArray(value)) {
            value = value[0];
        }

        if (!value) {
            return new Date();
        }

        if (typeof value === 'string') {
            value = value.trim();
        }

        if (Number(value)) {
            value = Number(value);
        }

        if (typeof value === 'string' || typeof value === 'number') {
            return new Date(value);
        }

        return new Date();
    }, [children]);

    const computedFormat = useMemo(() => {
        if (format) {
            return localeCtx.dateTimeFormats?.[format] || format;
        }

        return null;
    }, [localeCtx, format]);

    const value = useMemo(() => {
        try {
            if (fromNow) {
                return formatDistanceToNow(dateValue, {
                    addSuffix: true
                });
            }

            return formatFn(dateValue, computedFormat);
        } catch (error) {
            logError('date-time', error, {
                fromNow,
                dateValue,
                children,
                computedFormat
            });
        }

        return '';
    }, [fromNow, dateValue, computedFormat]);

    return (
        <time ref={ref} onClick={onClick}>{ value }</time>
    );
});

export default DateTime;
